// src/components/desk/PillOverflow.jsx — B614. A pill row that never overflows its card.
//
// Owner request 2026-08-09, from a screenshot of a property card: *"make these truncated group
// of pills a three-vertical-dot menu"*, and then the shape — *"it may be cutting one by one and
// adding to that group"*. Measured before building (30 card footers, live Desk): available width
// 186–198 px against 230 px of content, so 32–44 px hangs off the end of the six-pill cards.
//
// ── WHY A PREFIX AND NOT AN ITERATIVE HIDE ─────────────────────────────────────────────────
// The owner's "one at a time" describes the RESULT, not the algorithm. Hiding one pill, then
// re-measuring, then hiding another is a layout loop that flickers and — worse — can oscillate:
// removing a pill can free enough width to fit it again, and the row flaps forever. Each pill's
// natural width does not depend on its neighbours, so measuring them all ONCE and taking the
// longest prefix that fits produces exactly the same answer, in one pass, and cannot oscillate.
//
// ⚠️ The measurement pass renders every pill inline. That is done in `useLayoutEffect`, which
// runs BEFORE paint, so the overflowing state is never visible — do not move it to `useEffect`
// or the wide row will flash on every card.
//
// ── WHAT THIS DELIBERATELY DOES NOT DO ─────────────────────────────────────────────────────
// It collapses WITHIN one group, never across the card footer's three groups. Two reasons, both
// load-bearing and both already ruled on:
//   • The stacked footer is three lines by owner ruling (CARD_FOOTER_SPEC §1) and the wide form
//     reorders them with flex `order`, so across groups the visual order is not the DOM order.
//     Inside a group there is no reordering, so DOM order IS visual order and the prefix is
//     honest without measuring positions.
//   • The restrictions group is a horizontal SCROLLER whose comment records four traps,
//     including an iOS edge-swipe hazard. It is not wrapped by this and must not be.
//
// ⚠️ A pill moved into the menu is UNMOUNTED from the row and remounted inside the panel. Any
// pill holding its own open popover closes when the card is resized across the threshold. That
// is acceptable (resize is rare, and the pill is reachable again immediately) but it is why each
// item must carry a stable `key` — without one, React remounts every pill on every re-split.
//
// ⚠️ Each item MUST carry a `title`: in the menu the pill sits beside its label, and three of
// these glyphs are an identical `＋` (add meeting / rental terms / client link). The face can
// tell them apart by position; a menu row cannot.
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { pill, popoverPos, useCloseOnPageMove } from './primitives.jsx'
import { fitPrefix } from '../../lib/pillFit.js'

const MENU_W = 232

/**
 * items: [{ key, title, node }] — `node` is the rendered pill.
 * The row renders the longest prefix that fits; the remainder goes into a `⋮` popover.
 */
export function PillOverflow({ items, className = '', gap = 6 }) {
  const rowRef = useRef(null)
  const [widths, setWidths] = useState(null)   // null = not measured yet → render all
  const [avail, setAvail] = useState(0)
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  const close = () => setPos(null)

  // Re-measure when the SET of pills changes, not on every render: widths are a property of the
  // pills themselves. The signature is the keys, so adding/removing a pill re-measures while a
  // pure re-render (a price tick, a hover) does not.
  const sig = items.map(i => i.key).join('|')
  useLayoutEffect(() => { setWidths(null) }, [sig])

  useLayoutEffect(() => {
    const row = rowRef.current
    if (!row) return
    if (widths === null) {
      // Measurement pass — every pill is inline right now (see the render below).
      const w = [...row.children].map(c => c.getBoundingClientRect().width)
      setWidths(w)
      setAvail(row.getBoundingClientRect().width)
      return
    }
    // Already measured: just track the container. ResizeObserver rather than a window listener,
    // because a card can change width without the window doing so — the grid reflows when the
    // right-hand rail opens, and the Desk has a collapsible one.
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width
      if (w != null) setAvail(prev => (Math.abs(prev - w) < 0.5 ? prev : w))
    })
    ro.observe(row)
    setAvail(row.getBoundingClientRect().width)
    return () => ro.disconnect()
  }, [widths, sig])

  // How many fit. Computed, never stored: derived state that can disagree with its inputs is
  // how a row ends up showing a stale split after a resize.
  const shown = (widths && widths.length === items.length && avail > 0)
    ? fitPrefix(widths, avail, gap) : items.length

  useCloseOnPageMove(!!pos, close, panelRef)
  useEffect(() => {
    if (!pos) return
    const onDocClick = e => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      close()
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [pos])

  const hidden = widths === null ? [] : items.slice(shown)
  const inline = widths === null ? items : items.slice(0, shown)

  return (
    // ⚠️⚠️ `grow` IS THE MEASUREMENT, and THE ROW MUST NEVER BE SIZED BY ITS OWN CONTENT.
    // That single sentence is the whole lesson; it took four tries, and all four are recorded
    // because each looked correct and each failed differently:
    //   1. NOTHING. A content-sized flex item its parent never squeezes: `clientWidth` always
    //      equals the content, so the row can never be "too narrow". Measured `avail 148`
    //      against widths `40+39+57` (= 148 with gaps) and concluded everything fitted, forever.
    //   2. `flex-1`, i.e. `flex: 1 1 0%`. A basis of ZERO contributes nothing to a content-sized
    //      parent's intrinsic width, so the row collapsed to 76 px at a 1400 px viewport and
    //      hid pills that had plenty of room.
    //   3. `grow` while the footer still had three SEPARATE pill groups: the row expanded and
    //      shoved the next group against the card's right edge — "move that pill back to left".
    //      That was a symptom of the groups, not of `grow`; merging them (B638) removed it.
    //   4. `min-w-0` alone — content-sized-but-shrinkable. It folds correctly and **can never
    //      unfold**, which is the owner's *"it collapsed but didn't expand back"*. THE FOLD IS
    //      A LATCH: hiding pills shrinks the content, a content-sized row then reports a
    //      SMALLER width, and that smaller width re-justifies the fold. The row measured the
    //      consequence of its own decision and treated it as the cause.
    // `grow` is what breaks the loop: the row always fills the space its parent offers, so
    // `avail` is the space AVAILABLE and never a function of what is currently displayed. The
    // pills stay left-aligned inside it, so the extra width is empty space before whatever is
    // pinned right — not a gap between pills.
    // ⚠️ The parent must still be able to squeeze it (`min-w-0`, and no `shrink-0` ancestor) or
    // failure 1 returns. `overflow-hidden` means a wrong answer clips rather than distorting.
    <div ref={rowRef} className={`flex items-center gap-1.5 grow min-w-0 overflow-hidden ${className}`}
      /* DEV-only readout of the split (items/shown/avail/widths). It is what found the
         content-sized-row bug above — the row reported `avail 148` against widths summing to
         148, i.e. "everything fits", forever. Left in because the next person debugging this
         will want exactly it, and gated so production DOM stays clean.
         ⚠️ It cannot be read from a BACKGROUND tab: ResizeObserver deliveries ride the
         rendering steps, so a hidden tab freezes `avail` at its mount value and every resize
         reads as "no change". Assert `document.visibilityState === 'visible'` first — this
         session lost a diagnosis to exactly that, twice. */
      data-po={import.meta.env.DEV
        ? `${items.length}/${shown}/${Math.round(avail)}/${widths ? widths.map(Math.round).join('+') : 'null'}`
        : undefined}>
      {inline.map(it => <span key={it.key} className="shrink-0">{it.node}</span>)}
      {hidden.length > 0 && (<>
        <button ref={btnRef} type="button" title={t('desk_more')}
          aria-label={t('desk_more')}
          onClick={e => {
            e.stopPropagation()   // card faces are click-to-open; the menu must not also open the modal
            if (pos) return close()
            // Upward by preference, same as ShareMenu: this row is the card's floor, so
            // downward is where a phone viewport runs out.
            setPos(popoverPos(btnRef.current, MENU_W))
          }}
          className={pill('neutral', { door: true, extra: 'bg-background text-muted-foreground shrink-0' })}>
          <Icon name="more-vertical" />
        </button>
        {pos && createPortal(
          <div ref={panelRef} style={{ ...pos, width: MENU_W }} onClick={e => e.stopPropagation()}
            className="desk-popover fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-1.5 px-3 text-sm overflow-y-auto overscroll-contain">
            {hidden.map(it => (
              // The pill KEEPS its own behaviour here — this is the same node, relocated, not a
              // menu row that re-implements it. The label beside it is the only thing that tells
              // three identical `＋` glyphs apart once they leave the row.
              <div key={it.key} className="flex items-center gap-2 py-1.5 border-b border-border/40 last:border-0">
                <span className="shrink-0">{it.node}</span>
                <span className="min-w-0 truncate text-xs text-muted-foreground">{it.title}</span>
              </div>
            ))}
          </div>, document.body)}
      </>)}
    </div>
  )
}

// Convenience for callers assembling a row out of conditional pills: drops the falsy ones so a
// caller can write `pills(a && {...}, b && {...})` without a filter at every call site.
export const pills = (...xs) => xs.filter(Boolean)

export default PillOverflow
